import React, { useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/react-query';
import { useAuth } from './hooks/useAuth';
import { useAppData, useAppMutation } from './hooks/useAppQuery';
import { AppLayout } from './components/AppLayout';
import { PageView, ToastMessage } from './types';
import { generateNotifications } from './services/notificationService';

// Components (Reduced List)
import AuthPage from './components/AuthPage';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import PropertiesManager from './components/PropertiesManager';
import TenantsManager from './components/TenantsManager';
import DealsManager from './components/DealsManager';
import RenovationManager from './components/RenovationManager';
import Maintenance from './components/Maintenance';
import Analytics from './components/Analytics';
import ReportsCenter from './components/ReportsCenter';
import SettingsModal from './components/SettingsModal';
import SubscriptionModal from './components/SubscriptionModal';
import EmailVerificationGate from './components/EmailVerificationGate';
import { Loader2 } from 'lucide-react';

const AppContent: React.FC = () => {
  const { user, loading: authLoading, login, logout, refreshUser } = useAuth();
  const [page, setPage] = useState<PageView>('dashboard');
  const [showAuth, setShowAuth] = useState(false);
  const [lang, setLang] = useState<'en' | 'pt'>('en');
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showSub, setShowSub] = useState(false);

  // Legacy Data Hook (Still needed for Dashboard/Maintenance until Phase 4)
  const { data: userData, isLoading: dataLoading } = useAppData(user?.id);
  const mutation = useAppMutation(user?.id);

  const addToast = (type: 'success'|'error'|'info', title: string, message: string) => {
    setToasts(prev => [...prev, { id: Date.now().toString(), type, title, message }]);
  };

  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  // Legacy Update Handler
  const handleDataUpdate = async (updates: Partial<typeof userData>) => {
    try {
      await mutation.mutateAsync(updates);
    } catch (e) {
      addToast('error', 'Sync Failed', 'Could not save changes.');
    }
  };

  if (authLoading || (user && dataLoading)) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-10 h-10 text-emerald-500 animate-spin" /></div>;
  }

  if (!user) {
    return showAuth 
      ? <AuthPage onAuthDone={refreshUser} onBack={() => setShowAuth(false)} /> 
      : <LandingPage onNavigateToAuth={() => setShowAuth(true)} />;
  }

  // Email confirmation gate (SaaS)
  // If the user hasn't verified their email, keep them here.
  if (!user.emailVerified) {
    return <EmailVerificationGate onVerified={refreshUser} />;
  }

  const notifications = generateNotifications(userData?.properties || []);
  const commonProps = { lang, subscriptionPlan: user.subscription?.plan, onUpgradeRequired: () => setShowSub(true) };
  
  // Safe Fallbacks
  const properties = userData?.properties || [];
  
  const renderPage = () => {
    switch (page) {
      // Modern Modules (Autonomous)
      case 'dashboard': return <Dashboard properties={properties} tasks={userData?.tasks || []} notifications={notifications} entities={userData?.entities || []} selectedEntity="all" onSelectEntity={() => {}} {...commonProps} />;
      case 'properties': return <PropertiesManager lang={lang} />;
      case 'tenants': return <TenantsManager />;
      case 'deals': return <DealsManager />;
      
      // Legacy Modules (Still need cleanup in Phase 4)
      case 'maintenance': return <Maintenance lang={lang} />;
      case 'renovations': return <RenovationManager properties={properties} renovations={userData?.renovations || []} contractors={userData?.contractors || []} onUpdateRenovations={r => handleDataUpdate({ renovations: r })} onUpdateContractors={c => handleDataUpdate({ contractors: c })} {...commonProps} />;
      case 'analytics': return <Analytics properties={properties} {...commonProps} />;
      case 'reports': return <ReportsCenter properties={properties} user={user} />;
      
      default: return <Dashboard properties={properties} tasks={userData?.tasks || []} notifications={notifications} entities={userData?.entities || []} selectedEntity="all" onSelectEntity={() => {}} {...commonProps} />;
    }
  };

  return (
    <AppLayout 
      user={user} page={page} setPage={setPage} lang={lang} setLang={setLang}
      notifications={notifications} isSyncing={mutation.isPending} onLogout={logout}
      onOpenSettings={() => setShowSettings(true)} onOpenSubscription={() => setShowSub(true)}
      toasts={toasts} removeToast={removeToast}
    >
      {renderPage()}
      {showSettings && <SettingsModal user={user} invoices={userData?.invoices || []} onClose={() => setShowSettings(false)} onUpdateUser={refreshUser} onLogout={logout} onDataReset={refreshUser} />}
      {showSub && <SubscriptionModal isOpen={showSub} onClose={() => setShowSub(false)} currentPlan={user.subscription?.plan || 'Starter'} onSubscribe={async () => {}} />}
    </AppLayout>
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AppContent />
  </QueryClientProvider>
);

export default App;